/**
 * A scenario type read beside its protocol, which lights the type's decision paths. The pane leads with
 * the type's description and what the protocol prescribes, then the ways in (its scenarios, its bulk
 * runs), then the path as compact steps, each tool's expected result folded under its name.
 */

import { useState } from 'react';
import {
	AlertTriangle,
	ArrowRight,
	ChevronRight,
	GitBranch,
	History,
	ListTree,
	Route,
	Ticket,
	Wrench,
	type LucideIcon
} from 'lucide-react';
import { ContainedScrollArea } from '@/components/ui/contained-scroll-area';
import { cn } from '@/lib/utils';
import { PanelHeader } from '@/modules/simulator/sidebar/panel';
import { PanelState } from '@/modules/simulator/ui/panel-state';
import { Reveal } from '@/modules/simulator/ui/reveal';
import { StepRail, type RailStep } from '@/modules/simulator/ui/step-rail';
import type { ScenarioCounts, ScenarioTypeBranch, ScenarioTypeTool } from '@/services/ai-function.service';
import { readinessDetail } from '../readiness/format';
import { ReadinessBadge } from '../readiness/readiness-badge';
import type { TypeReadiness } from '../readiness/types';
import { branchTrail, decisionChain, endingPhrase, pathSteps, writesOf, type ScenarioType } from '../types';
import { countsLine } from './scenario-counts';

/** A tool by name; its step and what the protocol expects it to return open under the name. */
const ToolStep = ({ name, label, expect }: { name: string; label?: string; expect: string | undefined }) => {
	const [open, setOpen] = useState(false);
	const chip = <code className='rounded bg-gray-300 px-1 py-px font-mono text-[11px]'>{name}</code>;
	if (!label && !expect) return chip;
	return (
		<>
			<button
				type='button'
				onClick={() => setOpen((o) => !o)}
				aria-expanded={open}
				className='flex cursor-pointer items-center gap-1 text-left'
			>
				{chip}
				<ChevronRight
					className={cn('size-3 shrink-0 text-gray-800 transition-transform duration-200', open && 'rotate-90')}
					aria-hidden
				/>
			</button>
			<Reveal open={open}>
				<span className='mt-0.5 block text-[11px] leading-4 text-gray-800'>
					{label}
					{label && expect && <br />}
					{expect && <>→ {expect}</>}
				</span>
			</Reveal>
		</>
	);
};

/** A tool as a step on the rail. */
const toolStep = (key: string, tools: ScenarioTypeTool[], name: string, label?: string): RailStep => ({
	key,
	icon: Wrench,
	tone: 'text-amber-500',
	label: <ToolStep name={name} label={label} expect={tools.find((tool) => tool.name === name)?.expect} />
});

const endingStep = (branch: ScenarioTypeBranch): RailStep => ({
	key: 'ending',
	icon: ArrowRight,
	label: (
		<>
			<span className='text-gray-800'>Ends in {endingPhrase(branch)}</span>
			{branch.ending.text && <>: {branch.ending.text}</>}
		</>
	)
});

/** One decision path in the order it is taken: its tools and decisions, then where it ends. */
const pathRail = (branch: ScenarioTypeBranch, tools: ScenarioTypeTool[]): RailStep[] => [
	...pathSteps(branch).map(
		(step): RailStep =>
			step.kind === 'decision'
				? {
						key: step.nodeId,
						icon: GitBranch,
						label: (
							<>
								{step.label && <span className='text-gray-800'>{step.label}: </span>}
								{step.condition === 'otherwise' ? <em>none of the cases above</em> : step.condition}
							</>
						)
					}
				: toolStep(step.nodeId, tools, step.tool, step.label)
	),
	endingStep(branch)
];

/** An outcome several decision paths reach: the writes they make and where they end, then the paths. */
const OutcomeSteps = ({ branch, tools }: { branch: ScenarioTypeBranch; tools: ScenarioTypeTool[] }) => {
	const more = branch.decisionPathCount - branch.decisionPaths.length;
	return (
		<div className='flex flex-col gap-3' data-testid='outcome-trail'>
			<StepRail steps={[...writesOf(branch).map((name) => toolStep(name, tools, name)), endingStep(branch)]} />
			<div className='flex flex-col gap-1'>
				<p className='text-xs leading-4 text-gray-800'>Reached by {branch.decisionPathCount} paths</p>
				<ol className='flex flex-col gap-1'>
					{branch.decisionPaths.map((path) => {
						const chain = decisionChain(path.decisions);
						return (
							<li key={`${chain}|${path.nodeIds.join('>')}`} className='flex items-baseline gap-2 text-xs leading-snug'>
								<GitBranch className='size-3 shrink-0 translate-y-0.5 text-gray-800' aria-hidden />
								<span className='min-w-0 flex-1 text-text-body'>{chain}</span>
							</li>
						);
					})}
					{more > 0 && <li className='pl-5 text-xs leading-snug text-gray-800'>and {more} more</li>}
				</ol>
			</div>
		</div>
	);
};

const pathTitle = (branch: ScenarioTypeBranch): string => {
	if (branch.decisionPathCount > 1) {
		return branch.fallback ? 'This outcome (the fallback)' : 'This outcome and the paths that reach it';
	}
	return branch.fallback ? 'This path (the fallback)' : 'This path through the protocol';
};

/** A way into the type's material, as a row of the panel kit. */
const Entry = ({ icon, title, onClick }: { icon: LucideIcon; title: string; onClick: () => void }) => (
	<button type='button' onClick={onClick} className='group w-full cursor-pointer text-left transition-colors hover:bg-gray-100/60'>
		<PanelHeader
			icon={icon}
			title={title}
			trailing={
				<ArrowRight className='size-4 shrink-0 text-gray-900 transition-transform group-hover:translate-x-0.5' aria-hidden />
			}
		/>
	</button>
);

interface TypeOverviewProps {
	type: ScenarioType;
	counts: ScenarioCounts;
	/** Whether the type's latest finished bulk run clears it for promotion; absent until the verdicts load. */
	readiness?: TypeReadiness;
	onOpenScenarios: () => void;
	onOpenBulkRuns: () => void;
}

export const TypeOverview = ({
	type: { description, outcome, tools, branch },
	counts,
	readiness,
	onOpenScenarios,
	onOpenBulkRuns
}: TypeOverviewProps) => (
	<ContainedScrollArea>
		<div className='flex flex-col'>
			<div className='flex flex-col gap-3 px-4 py-3'>
				<p className='text-[13px] leading-4.5 font-medium text-text-heading'>{description}</p>
				<div className='flex flex-col gap-1'>
					<p className='text-xs leading-4 text-gray-800'>What the protocol prescribes</p>
					<p className='text-[13px] leading-4.5 text-text-body'>{outcome}</p>
				</div>
				{readiness && (
					<div className='flex flex-col gap-1' data-testid='type-readiness'>
						<p className='text-xs leading-4 text-gray-800'>Ready to promote</p>
						<div className='flex items-start gap-2'>
							<ReadinessBadge readiness={readiness} />
							<p className='text-xs leading-4 text-text-body'>{readinessDetail(readiness)}</p>
						</div>
					</div>
				)}
			</div>

			<div className='flex flex-col divide-y divide-gray-500 border-y border-gray-500'>
				<Entry icon={Ticket} title={countsLine(counts)} onClick={onOpenScenarios} />
				<Entry icon={History} title='Bulk runs' onClick={onOpenBulkRuns} />
			</div>

			<PanelHeader icon={Route} title={pathTitle(branch)} description={branchTrail(branch)} />
			<div className='flex flex-col gap-2 px-4 pb-4'>
				{branch.stale && (
					<p className='flex items-start gap-1.5 text-xs leading-4 text-amber-700'>
						<AlertTriangle className='mt-0.5 size-3.5 shrink-0' />
						<span>
							The protocol was recompiled since these types were generated, and its graph no longer has this
							outcome. Regenerate the types to realign them.
						</span>
					</p>
				)}
				{branch.decisionPathCount > 1 ? (
					<OutcomeSteps branch={branch} tools={tools} />
				) : (
					<div data-testid='branch-trail'>
						<StepRail steps={pathRail(branch, tools)} />
					</div>
				)}
				{branch.fallback && (
					<p className='text-xs leading-4 text-gray-800'>
						The path the protocol takes when none of its described cases apply.
					</p>
				)}
			</div>
		</div>
	</ContainedScrollArea>
);

/** Shown in the detail pane while no type is selected. */
export const TypeOverviewEmpty = () => (
	<PanelState icon={<ListTree className='size-5' />}>
		Pick a scenario type to read what the protocol prescribes for it, and to see its paths lit in the protocol.
	</PanelState>
);
